import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  Avatar,
  Button,
  DataTable,
  EmptyState,
  Icon,
  IconButton,
  Modal,
  PageHeader,
  SegmentedControl,
  Select,
  Skeleton,
  SkeletonText,
  useToast,
} from "@astrum/ui";
import { api } from "../lib/api.js";
import type { TrendBucket } from "../lib/api.js";
import { LineChart } from "../components/Charts.js";
import { toCsv, downloadCsv } from "../lib/csv.js";
import { usePageTitle } from "../lib/usePageTitle.js";
import "./daily-report.css";

/**
 * One person's full report history — what "View week" used to open as a
 * modal, now a real page a manager can navigate to, bookmark, and come back
 * to, the same way a task or a file gets its own address rather than only
 * existing inside whatever list happened to be open when it was clicked.
 *
 * Every calendar day from their first report to today is a row, not just
 * the days they filed one — a table that only shows what was submitted
 * cannot answer "did they miss any days," which is the actual question a
 * manager or HR opens this page to ask.
 */

interface TeamMember {
  id: string;
  displayName: string;
  avatarUrl: string | null;
}

interface Report {
  id: string;
  date: string;
  whatIDid: string;
  blockers: string | null;
  tomorrowsPlan: string | null;
}

/** A real report, or a placeholder standing in for a day nobody filed one. */
type DayRow = { date: string; report: Report | null };

/** On screen, the year is dropped for the common case (this year) to keep
 *  the Date column tight — a table of mostly-recent rows does not need it
 *  repeated on every line. Always kept in the CSV (formatDateForExport):
 *  a record read back months later should never be ambiguous about the
 *  year just because it was exported near a year boundary. */
function formatDate(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  const parsed = new Date(year!, month! - 1, day!);
  const sameYear = parsed.getFullYear() === new Date().getFullYear();
  return parsed.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: sameYear ? undefined : "numeric",
  });
}

function formatDateForExport(date: string): string {
  const [year, month, day] = date.split("-").map(Number);
  return new Date(year!, month! - 1, day!).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function addDays(date: string, days: number): string {
  const [year, month, day] = date.split("-").map(Number);
  const d = new Date(year!, month! - 1, day! + days);
  return d.toLocaleDateString("en-CA");
}

function todayKey(): string {
  return new Date().toLocaleDateString("en-CA");
}

/** Every day from the earliest report to today, each paired with its report
 *  or `null` for a gap. Not padded further back than their first report —
 *  a "No report" row for a week before someone ever started reporting would
 *  not be a real gap, just noise. */
function fillGaps(reports: Report[]): DayRow[] {
  if (reports.length === 0) return [];
  const byDate = new Map(reports.map((r) => [r.date, r]));
  const earliest = reports.reduce((min, r) => (r.date < min ? r.date : min), reports[0]!.date);

  const rows: DayRow[] = [];
  for (let date = earliest; date <= todayKey(); date = addDays(date, 1)) {
    rows.push({ date, report: byDate.get(date) ?? null });
  }
  return rows.reverse();
}

function exportPersonCsv(displayName: string, rows: DayRow[]): void {
  const csv = toCsv(
    ["Date", "Status", "What they did", "Blockers", "Tomorrow's plan"],
    rows,
    (row) => [
      formatDateForExport(row.date),
      row.report ? "Reported" : "No report",
      row.report?.whatIDid ?? "",
      row.report?.blockers ?? "",
      row.report?.tomorrowsPlan ?? "",
    ],
  );
  downloadCsv(`${displayName.toLowerCase().replace(/\s+/g, "-")}-reports.csv`, csv);
}

export function DailyReportPerson() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  // Kept even after the initial load so switching people (dropdown or
  // prev/next) never needs to refetch the roster — only that one person's
  // reports change.
  const [roster, setRoster] = useState<TeamMember[] | null>(null);
  const [reports, setReports] = useState<Report[] | null>(null);
  // Table cells clamp long text to a few lines so every row stays the same
  // height regardless of how much any one person wrote — the full report is
  // one click away instead.
  const [openDay, setOpenDay] = useState<DayRow | null>(null);

  // Trend chart controls, independent of the day-by-day table above — a
  // manager scanning for a pattern wants a coarser knob than "every day
  // since they started."
  const [trendBucket, setTrendBucket] = useState<"week" | "month">("week");
  const [trendMonths, setTrendMonths] = useState("3");
  const [trend, setTrend] = useState<TrendBucket[] | null>(null);

  useEffect(() => {
    api
      .get<{ team: TeamMember[] }>("/daily-reports/team")
      .then((res) => setRoster([...res.team].sort((a, b) => a.displayName.localeCompare(b.displayName))))
      .catch(() => toast("Could not load your team.", { tone: "danger" }));
  }, [toast]);

  useEffect(() => {
    if (!userId) return;
    setReports(null);
    api
      .get<{ reports: Report[] }>(`/daily-reports/${userId}`)
      .then((res) => setReports(res.reports))
      .catch(() => toast("Could not load that person's reports.", { tone: "danger" }));
  }, [userId, toast]);

  useEffect(() => {
    if (!userId) return;
    setTrend(null);
    api
      .get<{ buckets: TrendBucket[] }>(`/daily-reports/team/${userId}/trend?bucket=${trendBucket}&months=${trendMonths}`)
      .then((res) => setTrend(res.buckets))
      .catch(() => toast("Could not load the report-rate trend.", { tone: "danger" }));
  }, [userId, trendBucket, trendMonths, toast]);

  const member = roster === null ? undefined : roster.find((m) => m.id === userId) ?? null;
  usePageTitle(member?.displayName ?? "");
  const loading = roster === null || member === undefined || reports === null;
  const rows = useMemo(() => fillGaps(reports ?? []), [reports]);

  // Rate as a whole-number percentage — a bucket with no workdays at all
  // (theoretically possible with an all-holiday month) has no rate to plot,
  // so it draws as zero rather than NaN.
  const trendData = useMemo(
    () => (trend ?? []).map((b) => ({ label: b.label, value: b.rate === null ? 0 : Math.round(b.rate * 100) })),
    [trend],
  );

  const memberIndex = roster?.findIndex((m) => m.id === userId) ?? -1;
  const previous = roster && memberIndex > 0 ? roster[memberIndex - 1] : undefined;
  const next = roster && memberIndex >= 0 && memberIndex < roster.length - 1 ? roster[memberIndex + 1] : undefined;

  return (
    <div className="daily-report-person">
      <PageHeader
        className="daily-report-person__header"
        leading={
          <IconButton
            icon={<Icon name="chevron-left" size={18} />}
            label="Back to team reports"
            onClick={() => navigate("/daily-report/team")}
          />
        }
        title={
          member && roster ? (
            <span className="daily-report-person__title">
              <Avatar name={member.displayName} size={24} src={member.avatarUrl ?? undefined} />
              <select
                className="daily-report-person__switcher"
                value={member.id}
                aria-label="Switch to a different person"
                onChange={(e) => navigate(`/daily-report/team/${e.target.value}`)}
              >
                {roster.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </span>
          ) : (
            "Team reports"
          )
        }
        meta="Team reports"
        actions={
          <>
            {member && (
              <div className="daily-report-person__pager">
                <IconButton
                  icon={<Icon name="chevron-left" size={14} />}
                  label={previous ? `Previous: ${previous.displayName}` : "No previous person"}
                  size="sm"
                  disabled={!previous}
                  onClick={() => previous && navigate(`/daily-report/team/${previous.id}`)}
                />
                <IconButton
                  icon={<Icon name="chevron-right" size={14} />}
                  label={next ? `Next: ${next.displayName}` : "No next person"}
                  size="sm"
                  disabled={!next}
                  onClick={() => next && navigate(`/daily-report/team/${next.id}`)}
                />
              </div>
            )}
            {member && rows.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                icon={<Icon name="download" size={14} />}
                onClick={() => exportPersonCsv(member.displayName, rows)}
              >
                Export CSV
              </Button>
            )}
          </>
        }
      />

      <div className="daily-report-person__content">
        {loading ? (
          <SkeletonText lines={8} />
        ) : member === null ? (
          <EmptyState
            icon="people"
            title="Not found"
            description="This person doesn't report to you, or the account no longer exists."
            action={<Button onClick={() => navigate("/daily-report/team")}>Back to team reports</Button>}
          />
        ) : (
          <>
            <section className="daily-report-person__trend" aria-label="Report-rate trend">
              <div className="daily-report-person__trend-header">
                <h2>Report-rate trend</h2>
                <div className="daily-report-person__trend-controls">
                  <SegmentedControl
                    aria-label="Bucket by"
                    value={trendBucket}
                    onChange={(value) => setTrendBucket(value)}
                    options={[
                      { value: "week", label: "Week" },
                      { value: "month", label: "Month" },
                    ]}
                  />
                  <Select
                    aria-label="Months of history"
                    value={trendMonths}
                    onChange={(e) => setTrendMonths(e.target.value)}
                    className="daily-report-person__trend-range"
                  >
                    <option value="1">Last month</option>
                    <option value="3">Last 3 months</option>
                    <option value="6">Last 6 months</option>
                    <option value="12">Last 12 months</option>
                  </Select>
                </div>
              </div>
              {trend === null ? (
                <Skeleton height={200} />
              ) : trend.every((b) => b.workdays === 0) ? (
                <EmptyState icon="insights" title="Nothing to show" description="No workdays fall in this range yet." />
              ) : (
                <LineChart data={trendData} title={`${member.displayName}'s report rate (%) by ${trendBucket}`} />
              )}
            </section>

            {rows.length === 0 ? (
              <EmptyState icon="clock" title="No reports yet" description={`${member.displayName} hasn't filed a report yet.`} />
            ) : (
              <DataTable
                caption={`${member.displayName}'s daily reports`}
                density="compact"
                rows={rows}
                rowKey={(row) => row.date}
                onRowClick={(row) => row.report && setOpenDay(row)}
                empty={<EmptyState icon="clock" title="No reports yet" />}
                columns={[
                  {
                    key: "date",
                    header: "Date",
                    width: "120px",
                    rowHeader: true,
                    render: (row) => <span className="daily-report-person__date">{formatDate(row.date)}</span>,
                  },
                  {
                    key: "whatIDid",
                    header: "What they did",
                    render: (row) =>
                      row.report ? (
                        <span className="daily-report-person__cell">{row.report.whatIDid}</span>
                      ) : (
                        <span className="daily-report-team__muted">No report</span>
                      ),
                  },
                  {
                    key: "blockers",
                    header: "Blockers",
                    width: "220px",
                    render: (row) =>
                      row.report?.blockers ? (
                        <span className="daily-report-person__cell daily-report__summary-blocker">
                          {row.report.blockers}
                        </span>
                      ) : (
                        <span className="daily-report-team__muted">—</span>
                      ),
                  },
                  {
                    key: "tomorrowsPlan",
                    header: "Next",
                    render: (row) =>
                      row.report?.tomorrowsPlan ? (
                        <span className="daily-report-person__cell">{row.report.tomorrowsPlan}</span>
                      ) : (
                        <span className="daily-report-team__muted">—</span>
                      ),
                  },
                ]}
              />
            )}
          </>
        )}
      </div>

      <Modal open={openDay !== null} onClose={() => setOpenDay(null)} title={openDay ? formatDate(openDay.date) : undefined}>
        {openDay?.report && (
          <div className="daily-report-person__detail">
            <p className="daily-report-person__detail-body">{openDay.report.whatIDid}</p>
            {openDay.report.blockers && (
              <p className="daily-report__summary-blocker">
                <Icon name="warning" size={13} /> Blocked: {openDay.report.blockers}
              </p>
            )}
            {openDay.report.tomorrowsPlan && (
              <p className="daily-report__history-label">
                Next: <span>{openDay.report.tomorrowsPlan}</span>
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
